import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { Address } from '@app/sharedModule/models/address.model';
import { ApiService } from '@app/core/services/api/api.service';
import { countryList } from '@app/pages/checkout-app/configs/calendar-configs';
import { AddressService } from '@app/services/address/address.service';
import { TimeUnits } from '@utils/times-utils';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { ShopService } from '@app/services/shops/shop.service';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import moment from 'moment';

@Component({
  selector: 'custom-shipping-day',
  templateUrl: './custom-shipping-day.component.html',
  styleUrl: './custom-shipping-day.component.scss',
  standalone: false,
})
export class CustomShippingDayComponent implements OnInit, OnDestroy {
  private shopSubscription: Subscription;
  private nationalHolidaysRequestSubscription: Subscription;
  @Input() address: Address;
  selectedCategory: DropdownOption;
  closedDaysOption: DropdownOption[] = [
    { label: 'Holidays', value: 'holidays' },
    { label: 'Days of the Week', value: 'daysOfTheWeek' },
    { label: 'Custom Days', value: 'customDays' },
  ];

  selectedHolidayRegion: DropdownOption;
  holidayRegions: DropdownOption[] = [];

  selectedNationalHolidays: DropdownOption[] = [];
  nationalHolidays: DropdownOption[] = [];

  selectedDaysOfTheWeek: DropdownOption[] = [];
  daysOfTheWeek: DropdownOption[] = [
    { label: 'Mondays', value: '1' },
    { label: 'Tuesdays', value: '2' },
    { label: 'Wednesdays', value: '3' },
    { label: 'Thursdays', value: '4' },
    { label: 'Fridays', value: '5' },
    { label: 'Saturdays', value: '6' },
    { label: 'Sundays', value: '0' },
  ];

  selectedCustomDate: { startDate: Date; endDate: Date };
  selections: { data: any }[] = [];
  isLoading = false;
  isNationalHolidaysLoading = false;

  constructor(
    private addressService: AddressService,
    private apiService: ApiService,
    private toastService: ToastService,
    private shopService: ShopService,
  ) {}

  ngOnDestroy(): void {
    if (this.shopSubscription) {
      this.shopSubscription.unsubscribe();
    }
    this.nationalHolidaysRequestSubscription?.unsubscribe();
  }

  ngOnInit(): void {
    this.initHolidaysList();
    this.initClosedDaysInfo(this.address);
  }

  initClosedDaysInfo(address) {
    if (!address?.closedDaysInfo) {
      this.selections = [];
      return;
    }

    this.selections = Object.entries(address.closedDaysInfo)
      .map(([type, data]) => {
        // Handle both array and single object cases
        const items = Array.isArray(data) ? data : [data];
        return items.map((item) => ({
          data: { type, ...item },
        }));
      })
      .flat();
  }

  initHolidaysList() {
    this.holidayRegions = countryList().map((country) => ({
      label: country.name,
      value: country.value,
    }));
  }

  onCategoryChange($event) {
    this.selectedCategory = $event;
  }

  onHolidayRegionChange($event) {
    this.selectedHolidayRegion = $event;
    this.selectedNationalHolidays = [];
    this.nationalHolidays = [];

    this.nationalHolidaysRequestSubscription?.unsubscribe();
    this.isNationalHolidaysLoading = true;
    this.nationalHolidaysRequestSubscription = this.apiService
      .get(`address/holidaysList/${$event.value}`)
      .pipe(finalize(() => (this.isNationalHolidaysLoading = false)))
      .subscribe({
        next: (res) => {
          this.nationalHolidays = res;
        },
        error: (err) => {
          console.log('err', err);
        },
      });
  }

  onNationalHolidaysSelectionReplaced(allHolidayOptions: DropdownOption[]) {
    this.selectedNationalHolidays = [...allHolidayOptions];
  }

  onDaysOfTheWeekSelectionReplaced(allDayOptions: DropdownOption[]) {
    this.selectedDaysOfTheWeek = [...allDayOptions];
  }

  formatSelectedHolidaysCount = (selectedCount: number): string => `${selectedCount} holidays selected`;

  formatSelectedDaysCount = (selectedCount: number): string => `${selectedCount} days selected`;

  getClosedDayTypeLabel(closedDayType: string): string {
    return this.closedDaysOption.find((option) => option.value === closedDayType)?.label ?? closedDayType;
  }

  handleDateRangeChange(event) {
    const { end, start } = event;
    this.selectedCustomDate = { startDate: moment.utc(start).toDate(), endDate: moment.utc(end).toDate() };
  }

  isInsertAllFields(): boolean {
    switch (this.selectedCategory?.value) {
      case 'holidays':
        return (
          this.selectedHolidayRegion &&
          this.selectedNationalHolidays.length > 0 &&
          this.selectedNationalHolidays.filter((holiday) => holiday?.checked === true).length > 0
        );
      case 'daysOfTheWeek':
        return (
          this.selectedDaysOfTheWeek.length > 0 &&
          this.selectedDaysOfTheWeek.filter((day) => day?.checked === true).length > 0
        );
      case 'customDays':
        return this.selectedCustomDate !== undefined;
    }
  }

  getCurrentSelectionEntry() {
    switch (this.selectedCategory?.value) {
      case 'holidays':
        return {
          data: {
            type: 'holidays',
            holidayRegion: this.selectedHolidayRegion,
            nationalHolidays: this.selectedNationalHolidays.filter((holiday) => holiday?.checked === true),
          },
        };
      case 'daysOfTheWeek':
        return {
          data: {
            type: 'daysOfTheWeek',
            days: this.selectedDaysOfTheWeek.filter((day) => day?.checked === true),
          },
        };
      case 'customDays':
        return {
          data: {
            type: 'customDays',
            customDate: this.selectedCustomDate,
          },
        };
      default:
        return '';
    }
  }

  addSelection() {
    const entry = this.getCurrentSelectionEntry();
    if (entry) {
      this.selections.push(entry);
    }
    this.clearSelection();
  }

  saveSelections() {
    this.isLoading = true;

    // Group selections by type
    const groupedData = this.selections.reduce((acc, item) => {
      const { type, ...rest } = item.data;
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(rest);
      return acc;
    }, {});

    this.addressService
      .updateClosedDaysInfo({ locationId: this.address?.locationId, closedDaysInfo: groupedData })
      .subscribe({
        next: () => {
          this.toastService.showToast({
            message: 'Closed days info updated successfully',
            mode: 'success',
            duration: TimeUnits._SECOND_IN_MS * 3,
          });
          Object.assign(this.address, { closedDaysInfo: groupedData });
          this.updateShopAddress(this.address);
        },
        error: () => {
          this.toastService.showToast({
            message: 'Error while saving closed days info',
            mode: 'danger',
          });
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  updateShopAddress(updatedAddress: Address) {
    this.shopService.updateShopAddress(this.address?.id, updatedAddress);
  }

  onDeleteSelection(item: { description: string; data: any }) {
    this.selections = this.selections.filter((selection) => {
      if (selection.data.type !== item.data.type) return true;

      // For holidays, check if it's the same region and holidays
      if (selection.data.type === 'holidays') {
        return selection.data.holidayRegion.value !== item.data.holidayRegion.value;
      }

      // For days of the week, check if it's the same set of days
      if (selection.data.type === 'daysOfTheWeek') {
        return !this.areDaysEqual(selection.data.days, item.data.days);
      }

      // For custom days, check if it's the same date
      if (selection.data.type === 'customDays') {
        return selection.data.customDate !== item.data.customDate;
      }

      return true;
    });
  }

  private areDaysEqual(days1: DropdownOption[], days2: DropdownOption[]): boolean {
    if (days1.length !== days2.length) return false;
    return days1.every((day1) => days2.some((day2) => day2.value === day1.value));
  }

  getNationalHolidayLabels(selection: any): string {
    return selection?.data?.nationalHolidays?.map((holiday) => holiday.label).join(', ');
  }

  getHolidayRegionLabels(selection: any): string {
    const labels = selection?.data?.holidayRegion?.label;
    return labels;
  }

  getDaysOfTheWeekLabels(selection: any): string {
    return selection?.data?.days
      ?.map((day) => day.label)
      .filter((label) => !!label)
      .join(', ');
  }

  clearSelection() {
    this.selectedCategory = null;
    this.selectedHolidayRegion = null;
    this.selectedNationalHolidays = [];
    this.selectedDaysOfTheWeek = [];
    this.selectedCustomDate = null;
  }
}
